// auditProgressClaim.js — honest-green extended to NARRATION (arch-fable5 A2).
// @arch:pure-engine
//
// Fable 5 doctrine: "Before reporting progress, audit each claim against a tool result
// from this session. Report outcomes faithfully." This is the architectural
// generalization of honest-green from the DONE verdict to every user-facing status line.
//
// PURE engine (Tier 1 / Dependency Rule): no react, no window, no IPC, no network.
// It NEVER blocks a turn and NEVER inverts a verdict — it only ANNOTATES claims that
// lack a backing tool result, so the loop (or a human) can ground or retract them.
// The executable oracle keeps sole authority over DONE; this catches fabricated STATUS.

// Claim patterns, each tagged with the KIND of evidence that would back it.
// EN + PT-BR, conservative (a phrase must look like a status assertion, not a plan).
const CLAIM_PATTERNS = [
  // "tests pass", "build green", "all green", "testes passaram", "tudo verde"
  { kind: 'green', re: /\b(tests?|testes?|suite|build|lint|ci)\s+(pass(?:ed|es|aram|ou)?|green|verdes?|ok|clean)\b/i },
  { kind: 'green', re: /\ball\s+(tests?\s+)?green\b/i },
  { kind: 'green', re: /\btudo\s+verde\b/i },
  { kind: 'green', re: /\b(\d+\s+)?tests?\s+pass(?:ed|ing)?\b/i },
  // "wrote/created/edited <file.ext>", "criei/escrevi/editei <arquivo>"
  { kind: 'wrote', re: /\b(wrote|created|added|edited|updated|criei|escrevi|editei|gerei|adicionei|atualizei)\b[^.\n]{0,60}\.(?:js|jsx|ts|tsx|mjs|cjs|md|json|css|scss|html|sql|py|sh|yml|yaml)\b/i },
  // "fixed the bug", "corrigi", "resolvi", "consertei"
  { kind: 'fixed', re: /\b(fixed|resolved|patched|corrigi(?:do|da)?|resolvi(?:do|da)?|consertei|consertado)\b/i },
  // "done", "complete", "finished", "pronto", "concluído", "finalizado", "✅"
  { kind: 'done', re: /\b(done|completed?|finished|shipped|pronto|conclu[ií]d[oa]|finalizad[oa])\b/i },
  { kind: 'done', re: /[✅✔]/ },
];

// What tool-result NAMES count as evidence for each claim kind.
const EVIDENCE_NAME_RE = {
  green: /test|verif|lint|smoke|type|bash|run.?command|exec|oracle|check/i,
  wrote: /write|edit|create.?file|str.?replace|multi.?edit|apply.?patch|save/i,
  // 'fixed' and 'done' accept any successful tool result as evidence that real work happened.
  fixed: /.*/,
  done: /.*/,
};

// Auditoria 07-06: um run_command exit-0 qualquer (`ls`, `git commit`) lastreava
// "testes verdes". Executor genérico agora só vale como evidência green se o
// COMANDO parecer um check; tools nomeadas (oracle, verification) valem pelo nome.
const GENERIC_EXEC_RE = /run.?command|bash|exec|shell/i;
const CHECK_CMD_RE = /\b(test|tests|vitest|jest|lint|eslint|tsc|typecheck|build|check|smoke|verify|pytest)\b/i;

function backsClaim(kind, r) {
  if (!EVIDENCE_NAME_RE[kind].test(r.name)) return false;
  if (kind === 'green' && GENERIC_EXEC_RE.test(r.name)) return CHECK_CMD_RE.test(r.command || '');
  return true;
}

function normalizeResult(tr) {
  if (!tr || typeof tr !== 'object') return null;
  const name = String(tr.name ?? tr.tool ?? tr.tool_name ?? tr.toolName ?? '');
  const id = tr.id ?? tr.tool_use_id ?? tr.toolUseId ?? tr.tool_call_id ?? null;
  const isError = tr.is_error === true || tr.isError === true || tr.ok === false || tr.error != null;
  const command = typeof tr.command === 'string' ? tr.command : '';
  return { name, id, isError, command };
}

/**
 * Audit a user-facing message's progress claims against this turn's tool results.
 *
 * @param {string} text - the assistant message about to be surfaced to the user.
 * @param {Array<object>} toolResults - tool results produced during the current turn.
 *   Each may carry { id|tool_use_id, name|tool, is_error|ok|error }.
 * @returns {{ flags: Array<{claim:string, kind:string, backed:boolean, evidenceId:(string|null)}>,
 *            hasUnbacked: boolean }}
 *   `flags` lists every detected status claim with whether a tool result backs it.
 *   ADVISORY ONLY — callers annotate; they must not block or invert a verdict.
 */
export function auditProgressClaim(text, toolResults) {
  if (typeof text !== 'string' || text.length === 0) {
    return { flags: [], hasUnbacked: false };
  }
  const results = Array.isArray(toolResults)
    ? toolResults.map(normalizeResult).filter(Boolean)
    : [];
  const successful = results.filter((r) => !r.isError);

  const flags = [];
  const seen = new Set();
  for (const { kind, re } of CLAIM_PATTERNS) {
    const m = text.match(re);
    if (!m) continue;
    const claim = m[0].trim();
    const dedupKey = `${kind}:${claim.toLowerCase()}`;
    if (seen.has(dedupKey)) continue;
    seen.add(dedupKey);

    const evidence = successful.find((r) => backsClaim(kind, r));
    flags.push({
      claim,
      kind,
      backed: Boolean(evidence),
      evidenceId: evidence ? evidence.id : null,
    });
  }

  return { flags, hasUnbacked: flags.some((f) => !f.backed) };
}

export default auditProgressClaim;

// F13-E14-T01 — fábrica dos claim-gates do spine (extraída pro dp-loc do
// agentLoop). auditAndFlag segue ADVISORY (anota); os checkers alimentam os
// gates COM DENTES do classifyToollessReply: 'green' já bloqueava
// (unbacked_green_claim); 'wrote' ganha o nudge tool-before-claim (leak
// Fable-5: "If you don't use the tool, you are lying to them").
export function makeClaimGates({ getTurnToolResults, onEvent, progressAuditOn }) {
  const auditAndFlag = (text) => {
    if (!progressAuditOn || !text) return;
    const { flags, hasUnbacked } = auditProgressClaim(text, getTurnToolResults());
    if (hasUnbacked) onEvent({ type: 'honest_progress_flag', flags: flags.filter((f) => !f.backed) });
  };
  const unbacked = (text, kind) => {
    if (!text) return false;
    const { flags } = auditProgressClaim(text, getTurnToolResults());
    return flags.some((f) => f.kind === kind && !f.backed);
  };
  return {
    auditAndFlag,
    claimsUnbackedGreen: (text) => unbacked(text, 'green'),
    claimsUnbackedWrite: (text) => unbacked(text, 'wrote'),
  };
}
